import React, { useState, useEffect } from 'react';
import baseURL from './baseUrl';
import logo from './logo.new1.png';

const LangdiWeightPage = () => {
  const [view, setView] = useState('list'); // 'list', 'create', 'conversions', 'edit'
  const [data, setData] = useState([]); // Available langdi weight entries
  const [selectedTransactions, setSelectedTransactions] = useState([]); // Selected transaction IDs
  const [currentConversion, setCurrentConversion] = useState(null); // Current conversion being created/edited
  const [conversions, setConversions] = useState([]); // All conversions
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [finalPureWeight, setFinalPureWeight] = useState('');
  const [logoBase64, setLogoBase64] = useState(null);
  const [editingTouch, setEditingTouch] = useState({}); // Track which touch inputs are being edited
  const [fromDate, setFromDate] = useState(''); // Filter from date
  const [toDate, setToDate] = useState(''); // Filter to date

  // Fetch transactions ready for conversion
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${baseURL}/api/conversions/transactions-for-conversion`);
        const data = await response.json();
        console.log('Fetched transactions for conversion:', data);
        setData(data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error fetching data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch all conversions
  useEffect(() => {
    const fetchConversions = async () => {
      try {
        const response = await fetch(`${baseURL}/api/conversions/all`);
        const data = await response.json();
        console.log('Fetched conversions:', data);
        setConversions(data);
      } catch (err) {
        console.error('Error fetching conversions:', err);
      }
    };
    fetchConversions();
  }, []);

  // Convert logo to base64
  useEffect(() => {
    const convertLogoToBase64 = async () => {
      try {
        const response = await fetch(logo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error converting logo to base64:', error);
      }
    };
    convertLogoToBase64();
  }, []);

  // Handle checkbox selection
  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  // Filter data by date range
  const filterDataByDateRange = (dataArray) => {
    if (!fromDate && !toDate) {
      return dataArray; // No filter applied
    }

    return dataArray.filter(transaction => {
      if (!transaction.date) return false;
      
      const transactionDate = new Date(transaction.date);
      transactionDate.setHours(0, 0, 0, 0); // Reset time to start of day
      
      if (fromDate && toDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999); // End of day
        return transactionDate >= from && transactionDate <= to;
      } else if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        return transactionDate >= from;
      } else if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        return transactionDate <= to;
      }
      
      return true;
    });
  };

  // Handle select all in date range
  const handleSelectAllInDateRange = () => {
    const filteredData = filterDataByDateRange(data);
    const filteredTransactionIds = filteredData.map(transaction => transaction._id);
    setSelectedTransactions(filteredTransactionIds);
  };

  // Handle clear date filter
  const handleClearDateFilter = () => {
    setFromDate('');
    setToDate('');
    setSelectedTransactions([]);
  };

  // Handle "Add" button - create conversion draft
  const handleCreateConversion = async () => {
    if (selectedTransactions.length === 0) {
      alert('Please select at least one transaction');
      return;
    }

    try {
      const response = await fetch(`${baseURL}/api/conversions/create-draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ transactionIds: selectedTransactions })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create conversion');
      }

      const conversionData = await response.json();
      console.log('Conversion draft created:', conversionData);
      setCurrentConversion(conversionData);
      setView('create');
      setSelectedTransactions([]);
    } catch (error) {
      console.error('Error creating conversion:', error);
      alert('Error creating conversion: ' + error.message);
    }
  };

  // Handle "Convert" button on creation page - save conversion (pending)
  const handleSaveConversion = async () => {
    if (!currentConversion) return;

    try {
      const response = await fetch(`${baseURL}/api/conversions/save-draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transactionIds: currentConversion.transactionIds,
          conversionNumber: currentConversion.conversionNumber,
          receiptNo: currentConversion.receiptNo,
          entries: currentConversion.entries,
          totalCalculatedPureWeight: currentConversion.totalCalculatedPureWeight
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save conversion');
      }

      alert('Conversion saved successfully!');
      setView('conversions');
      setCurrentConversion(null);
      
      // Refresh conversions list
      const convResponse = await fetch(`${baseURL}/api/conversions/all`);
      const convData = await convResponse.json();
      setConversions(convData);
      
      // Refresh available transactions
      const transResponse = await fetch(`${baseURL}/api/conversions/transactions-for-conversion`);
      const transData = await transResponse.json();
      setData(transData);
    } catch (error) {
      console.error('Error saving conversion:', error);
      alert('Error saving conversion: ' + error.message);
    }
  };

  // Handle edit conversion
  const handleEditConversion = async (conversion) => {
    try {
      // First, refresh conversions list to get the latest conversion data
      const convResponse = await fetch(`${baseURL}/api/conversions/all`);
      let latestConversion = conversion;
      if (convResponse.ok) {
        const allConversions = await convResponse.json();
        const foundConversion = allConversions.find(c => 
          c.conversionNumber === conversion.conversionNumber || 
          c._id === conversion._id
        );
        if (foundConversion) {
          latestConversion = foundConversion;
        }
      }
      
      setCurrentConversion(latestConversion);
      setFinalPureWeight(latestConversion.finalPureWeight || '');
    setView('edit');
    
      // Fetch transactions to get the latest touch values and transaction types
      // This ensures we can identify 'adding' type transactions and get updated touch values
      if (latestConversion.transactionIds && latestConversion.transactionIds.length > 0) {
      try {
        // Fetch all transactions and filter by the IDs we need
        const allTransResponse = await fetch(`${baseURL}/api/transactions/`);
        if (allTransResponse.ok) {
          const allTransactions = await allTransResponse.json();
            const transactionIdStrings = latestConversion.transactionIds.map(id => id?.toString());
          const neededTransactions = allTransactions.filter(t => 
            transactionIdStrings.includes(t._id?.toString())
          );
            
            // Update conversion entries with latest touch values from transactions
            if (neededTransactions.length > 0) {
              const updatedEntries = latestConversion.entries.map(entry => {
                const transaction = neededTransactions.find(t => 
                  t._id?.toString() === entry.transactionId?.toString()
                );
                if (transaction && transaction.entries && transaction.entries[entry.entryIndex] !== undefined) {
                  const transactionEntry = transaction.entries[entry.entryIndex];
                  // Update touch and calculated pure weight from transaction
                  const updatedTouch = transactionEntry.touch;
                  const updatedPureWeight = updatedTouch !== null && updatedTouch !== undefined && !isNaN(updatedTouch)
                    ? Number(((entry.langdiWeight * updatedTouch) / 100).toFixed(3))
                    : null;
                  return {
                    ...entry,
                    touch: updatedTouch,
                    calculatedPureWeight: updatedPureWeight
                  };
                }
                return entry;
              });
              
              // Recalculate total pure weight
              const totalCalculatedPureWeight = calculateTotalPureWeight(updatedEntries);
              
              setCurrentConversion({
                ...latestConversion,
                entries: updatedEntries,
                totalCalculatedPureWeight: Number(totalCalculatedPureWeight.toFixed(3))
              });
            }
          
          // Add fetched transactions to data array for reference
          setData(prevData => {
            const existingIds = new Set(prevData.map(t => t._id?.toString()));
            const newTransactions = neededTransactions.filter(t => 
              !existingIds.has(t._id?.toString())
            );
            return [...prevData, ...newTransactions];
          });
        }
      } catch (error) {
        console.warn('Could not fetch transactions for conversion (non-critical):', error);
      }
      }
    } catch (error) {
      console.error('Error loading conversion:', error);
      // Fallback to using the passed conversion if fetch fails
      setCurrentConversion(conversion);
      setFinalPureWeight(conversion.finalPureWeight || '');
      setView('edit');
    }
  };

  // Handle finalize conversion - update with actual pure weight and update stock
  const handleFinalizeConversion = async () => {
    if (!currentConversion) return;

    if (!finalPureWeight || isNaN(parseFloat(finalPureWeight))) {
      alert('Please enter a valid final pure weight');
      return;
    }

    try {
      const response = await fetch(`${baseURL}/api/conversions/finalize/${currentConversion.conversionNumber}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ finalPureWeight: parseFloat(finalPureWeight) })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to finalize conversion');
      }

      const result = await response.json();
      console.log('Conversion finalized:', result);
      alert('Conversion finalized successfully! Stock updated.');
      
      setView('conversions');
      setCurrentConversion(null);
      setFinalPureWeight('');
      
      // Refresh conversions list
      const convResponse = await fetch(`${baseURL}/api/conversions/all`);
      const convData = await convResponse.json();
      setConversions(convData);
      
      // Refresh available transactions
      const transResponse = await fetch(`${baseURL}/api/conversions/transactions-for-conversion`);
      const transData = await transResponse.json();
      setData(transData);
    } catch (error) {
      console.error('Error finalizing conversion:', error);
      alert('Error finalizing conversion: ' + error.message);
    }
  };

  // Helper function to safely format numbers
  const formatNumber = (value, decimals = 3, defaultValue = '0.000') => {
    if (value === null || value === undefined) return defaultValue;
    return typeof value === 'number' ? value.toFixed(decimals) : defaultValue;
  };

  // Function to format date as "DD/MM/YYYY"
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) {
      return 'Invalid Date';
    }
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Calculate total langdi weight from entries
  const calculateTotalLangdiWeight = (entries) => {
    return entries.reduce((sum, entry) => sum + (entry.langdiWeight || 0), 0);
  };

  // Calculate total pure weight from entries (only when touch is set)
  const calculateTotalPureWeight = (entries) => {
    return entries.reduce((sum, entry) => {
      return sum + (entry.calculatedPureWeight || 0);
    }, 0);
  };

  // Handle touch update for a transaction entry
  // Works for both pending and completed conversions - only updates calculations, NOT stock
  const handleUpdateTouch = async (transactionId, entryIndex, newTouch, transactionType) => {
    try {
      // Allow null touch (for clearing) or valid number
      const touchValue = newTouch !== null && newTouch !== undefined && !isNaN(newTouch) 
        ? parseFloat(newTouch) 
        : null;

      // Update backend - only updates transaction touch, does NOT update stock
      const response = await fetch(`${baseURL}/api/transactions/update-transaction-touch`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transactionId,
          entryIndex,
          touch: touchValue
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update touch');
      }

      // Calculate pure weight based on touch value
      const calculatedPureWeight = touchValue !== null && !isNaN(touchValue)
        ? null // Will be calculated per entry based on langdiWeight
        : null;

      // Update local state
      setData(prevData => {
        return prevData.map(transaction => {
          if (transaction._id === transactionId) {
            const updatedEntries = transaction.entries.map((entry, idx) => {
              if (idx === entryIndex) {
                const entryPureWeight = touchValue !== null && !isNaN(touchValue)
                  ? Number(((entry.langdiWeight * touchValue) / 100).toFixed(3))
                  : null;
                return {
                  ...entry,
                  touch: touchValue,
                  calculatedPureWeight: entryPureWeight
                };
              }
              return entry;
            });
            
            const totalCalculatedPureWeight = calculateTotalPureWeight(updatedEntries);
            
            return {
              ...transaction,
              entries: updatedEntries,
              totalCalculatedPureWeight: Number(totalCalculatedPureWeight.toFixed(3))
            };
          }
          return transaction;
        });
      });

      // Update currentConversion if it exists (works for both pending and completed)
      if (currentConversion) {
        setCurrentConversion(prevConversion => {
          if (!prevConversion) return prevConversion;
          
          const updatedEntries = prevConversion.entries.map((entry, idx) => {
            // Find matching entry by transaction ID and index
            if (entry.transactionId === transactionId && entry.entryIndex === entryIndex) {
              const entryPureWeight = touchValue !== null && !isNaN(touchValue)
                ? Number(((entry.langdiWeight * touchValue) / 100).toFixed(3))
                : null;
              return {
                ...entry,
                touch: touchValue,
                calculatedPureWeight: entryPureWeight
              };
            }
            return entry;
          });
          
          const totalCalculatedPureWeight = calculateTotalPureWeight(updatedEntries);
          
          return {
            ...prevConversion,
            entries: updatedEntries,
            totalCalculatedPureWeight: Number(totalCalculatedPureWeight.toFixed(3))
          };
        });
      }

      // If conversion is completed, also update the conversion log entry
      if (currentConversion && currentConversion.status === 'completed') {
        try {
          // Update conversion log entry touch and recalculated pure weight (does NOT update stock)
          const updateConvResponse = await fetch(`${baseURL}/api/conversions/update-entry-touch/${currentConversion.conversionNumber}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              transactionId,
              entryIndex,
              touch: touchValue
            })
          });

          if (updateConvResponse.ok) {
            const result = await updateConvResponse.json();
            if (result.conversion) {
              setCurrentConversion(result.conversion);
            }
          }
        } catch (convError) {
          console.warn('Could not update conversion log entry (non-critical):', convError);
          // Non-critical - conversion log update failed but transaction is updated
        }
      }

      // Refresh data to get latest from server
      const refreshResponse = await fetch(`${baseURL}/api/conversions/transactions-for-conversion`);
      const refreshedData = await refreshResponse.json();
      setData(refreshedData);
    } catch (error) {
      console.error('Error updating touch:', error);
      alert('Error updating touch: ' + error.message);
    }
  };

  // Handle print/download PDF
  const handlePrint = (conversion) => {
    if (!logoBase64) {
      console.error('Logo not yet loaded');
      return;
    }

    const conversionData = conversion || currentConversion;
    if (!conversionData) {
      console.error('No conversion data available');
      return;
    }

    const totalLangdi = calculateTotalLangdiWeight(conversionData.entries);
    const totalPure = conversionData.totalCalculatedPureWeight || 0;
    const finalPure = conversionData.finalPureWeight || null;

    const printWindow = document.createElement('iframe');
    printWindow.style.position = 'absolute';
    printWindow.style.width = '0';
    printWindow.style.height = '0';
    printWindow.style.border = 'none';
    document.body.appendChild(printWindow);

    const doc = printWindow.contentWindow.document;
    doc.open();
    doc.write('<html><head><title>Conversion Receipt</title>');
    doc.write('<style>');
    doc.write('body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f9f9f9; }');
    doc.write('@page { margin: 0; }');
    doc.write('.container { width: 80%; margin: auto; padding: 20px; background: #fff; border: 1px solid #ccc; border-radius: 8px; }');
    doc.write('h1 { text-align: center; font-size: 14px; margin-bottom: 0px; }');
    doc.write('.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }');
    doc.write('.logo { width: 130px; height: auto; object-fit: contain; filter:contrast(220%); }');
    doc.write('.table { width: 100%; border-collapse: collapse; margin-top: 20px; }');
    doc.write('.table th, .table td { border: 1px solid #000; padding: 8px; text-align: center; }');
    doc.write('.table th { background-color: #f2f2f2; }');
    doc.write('.totals { margin-top: 20px; width: 100%; border-collapse: collapse; }');
    doc.write('.total-row { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #000; }');
    doc.write('.total-row > span { padding: 8px; border-right: 1px solid #000; }');
    doc.write('.total-row > span:first-child { font-weight: bold; text-align: right; background: #f2f2f2; }');
    doc.write('.total-row > span:last-child { text-align: right; border-right: none; }');
    doc.write('.meta-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 6px 0; font-size: 12px; }');
    doc.write('.meta-item { }');
    doc.write('.date-block { font-size: 12px; line-height: 1.2; display: grid; grid-template-columns: auto auto; column-gap: 6px; row-gap: 2px; justify-content: end; text-align: left; }');
    doc.write('.date-label { font-weight: 600; }');
    doc.write('.info-lines { font-size: 13px; margin: 8px 0 10px 0; font-weight: 600; }');
    doc.write('.info-row { display: grid; grid-template-columns: 1fr 1fr; column-gap: 12px; }');
    doc.write('.info-item { text-align: left; }');
    doc.write('.footer-line { margin-top: 8px; text-align: left; font-size: 10px; color: #333; }');
    doc.write('</style>');
    doc.write('</head><body>');

    doc.write('<div class="container">');
    doc.write('<h1>|| SHREE GANESHAY NAMH ||</h1>');
    doc.write('<div class="header">');
    doc.write(`<img src="${logoBase64}" class="logo" alt="Logo">`);
    doc.write(`<div class="date-block">` +
      `<div class="date-label">Date:</div><div>${formatDate(conversionData.date)}</div>` +
      `<div class="date-label">Time:</div><div>${new Date(conversionData.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>` +
      `<div class="date-label">Name:</div><div>Manoj Shinde</div>` +
      `<div class="date-label">Mobile:</div><div>8551881892</div>` +
    `</div>`);
    doc.write('</div>');

    doc.write('<div class="meta-row">');
    doc.write(`<div class="meta-item"><strong>Conversion Receipt No:</strong> <span style="font-weight: bold; font-size: 18px; color: #000;">${conversionData.receiptNo || 'N/A'}</span><br><strong>Conversion Number:</strong> <span style="font-weight: bold; color: #000;">${conversionData.conversionNumber || 'N/A'}</span></div>`);
    doc.write('</div>');

    doc.write('<table class="table">');
    doc.write('<thead>');
    doc.write('<tr><th>Langdi Weight (gms)</th><th>Touch</th><th>Pure Weight (gms)</th><th>Receipt No</th><th>Client Name</th></tr>');
    doc.write('</thead>');
    doc.write('<tbody>');

    conversionData.entries.forEach((entry) => {
      doc.write('<tr>');
      doc.write(`<td>${formatNumber(entry.langdiWeight, 3)}</td>`);
      doc.write(`<td>${entry.touch !== null ? formatNumber(entry.touch, 2) + '%' : 'N/A'}</td>`);
      doc.write(`<td>${entry.calculatedPureWeight !== null ? formatNumber(entry.calculatedPureWeight, 3) : 'N/A'}</td>`);
      doc.write(`<td>${entry.receiptNo || 'N/A'}</td>`);
      doc.write(`<td>${entry.clientName || 'N/A'}</td>`);
      doc.write('</tr>');
    });

    doc.write('</tbody>');
    doc.write('</table>');

    doc.write('<div class="totals">');
    doc.write('<div class="total-row">');
    doc.write('<span>Total Langdi Weight:</span>');
    doc.write(`<span>${formatNumber(totalLangdi, 3)} gms</span>`);
    doc.write('</div>');
    doc.write('<div class="total-row">');
    doc.write('<span>Total Calculated Pure Weight:</span>');
    doc.write(`<span>${formatNumber(totalPure, 3)} gms</span>`);
    doc.write('</div>');
    if (finalPure !== null) {
      doc.write('<div class="total-row">');
      doc.write('<span>Final Pure Weight:</span>');
      doc.write(`<span>${formatNumber(finalPure, 3)} gms</span>`);
      doc.write('</div>');
    }
    doc.write('<div class="total-row">');
    doc.write('<span>Status:</span>');
    doc.write(`<span>${conversionData.status === 'completed' ? 'Completed' : 'Pending'}</span>`);
    doc.write('</div>');
    doc.write('</div>');

    doc.write('<div class="info-lines">');
    doc.write('<div class="info-row">');
    doc.write('<div class="info-item">* हमारे यहाँ लंगड़ी एक्सचेंज करके मिलता है</div>');
    doc.write('<div class="info-item">* RSBL गोल्ड कॉइन मिलता है</div>');
    doc.write('</div>');
    doc.write('<div class="info-row">');
    doc.write('<div class="info-item">* AQUA REFINE 995 और 999 मिलता है</div>');
    doc.write('<div class="info-item">* गोल्ड और सिल्वर मेल्ट किया जाता है</div>');
    doc.write('</div>');
    doc.write('</div>');

    doc.write('<div class="footer-line">powered by Riddhi Infotech - 7499391121</div>');
    doc.write('</div>');
    doc.write('</body></html>');
    doc.close();

    printWindow.onload = function() {
      printWindow.contentWindow.focus();
      printWindow.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(printWindow);
      }, 1000);
    };
  };

  // Render: List of available langdi weights
  if (view === 'list') {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="text-center mb-0">Langdi Weight Conversion</h1>
          <div>
            <button 
              className="btn btn-secondary me-2" 
              onClick={() => setView('conversions')}
            >
              View All Conversions
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleCreateConversion}
              disabled={selectedTransactions.length === 0}
            >
              Add Selected ({selectedTransactions.length})
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {error && <p className="text-danger text-center">{error}</p>}

        {!loading && !error && (
          <>
            {/* Date Range Filter */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-3">Filter by Date Range</h5>
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label htmlFor="fromDate" className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="fromDate"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="toDate" className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="toDate"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <button
                      className="btn btn-success me-2"
                      onClick={handleSelectAllInDateRange}
                      disabled={!fromDate && !toDate}
                    >
                      Select All in Date Range
                    </button>
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleClearDateFilter}
                    >
                      Clear Filter
                    </button>
                    {(fromDate || toDate) && (
                      <span className="text-muted">
                        Showing {filterDataByDateRange(data).length} of {data.length} items
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '50px' }}>Select</th>
                  <th>Date</th>
                  <th>Receipt No</th>
                  <th>Client Name</th>
                  <th>Langdi Weight (gms)</th>
                  <th>Touch</th>
                  <th>Calculated Pure (gms)</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filteredData = filterDataByDateRange(data);

                  if (filteredData.length === 0) {
                    return (
                  <tr>
                        <td colSpan="7" className="text-center">
                          {data.length === 0
                            ? 'No langdi weight entries available for conversion'
                            : 'No items found in the selected date range'}
                        </td>
                  </tr>
                    );
                  }

                  return filteredData.flatMap((transaction) => {
                    // Flatten entries for display
                    const entries = transaction.entries || [];

                    return entries.map((entry, index) => {
                      const entryId = `${transaction._id}-${index}`;
                      const isSelected = selectedTransactions.includes(transaction._id);
                      
                      return (
                        <tr key={entryId}>
                          {index === 0 && (
                            <>
                              <td rowSpan={entries.length}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSelectTransaction(transaction._id)}
                                />
                              </td>
                              <td rowSpan={entries.length}>{formatDate(transaction.date)}</td>
                              <td rowSpan={entries.length}>{transaction.receiptNo}</td>
                              <td rowSpan={entries.length}>{transaction.clientName || 'N/A'}</td>
                            </>
                          )}
                          <td>{formatNumber(entry.langdiWeight, 3)}</td>
                          <td>
                            {transaction.type === 'adding' ? (
                              (() => {
                                // Check if touch is already set - if it is, show as read-only
                                const touchIsSet =
                                  entry.touch !== null &&
                                  entry.touch !== undefined &&
                                  entry.touch !== '' &&
                                  !isNaN(parseFloat(entry.touch));

                                if (touchIsSet) {
                                  return entry.touch !== null
                                    ? `${formatNumber(entry.touch, 2)}%`
                                    : 'N/A';
                                }

                                // Touch is not set, allow editing
                                return (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                    value={
                                      editingTouch[`${transaction._id}-${index}`] !== undefined
                                        ? editingTouch[`${transaction._id}-${index}`]
                                        : ''
                                    }
                                placeholder="Enter touch"
                                step="0.01"
                                min="0"
                                max="100"
                                onChange={(e) => {
                                      // Update local state only, don't call API yet
                                      const newValue = e.target.value;
                                      setEditingTouch((prev) => ({
                                        ...prev,
                                        [`${transaction._id}-${index}`]: newValue,
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      // Only update on blur to prevent closing input
                                      const touchValue =
                                        e.target.value === '' ? null : parseFloat(e.target.value);
                                  handleUpdateTouch(transaction._id, index, touchValue, transaction.type);
                                      // Clear local editing state after blur
                                      setEditingTouch((prev) => {
                                        const newState = { ...prev };
                                        delete newState[`${transaction._id}-${index}`];
                                        return newState;
                                      });
                                    }}
                                    onKeyDown={(e) => {
                                      // Handle Enter key to blur
                                      if (e.key === 'Enter') {
                                        e.target.blur();
                                      }
                                }}
                                style={{ width: '80px' }}
                              />
                                );
                              })()
                            ) : entry.touch !== null ? (
                              `${formatNumber(entry.touch, 2)}%`
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td>
                            {entry.calculatedPureWeight !== null
                              ? formatNumber(entry.calculatedPureWeight, 3)
                              : 'N/A'}
                          </td>
                        </tr>
                      );
                    });
                  });
                })()}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    );
  }

  // Render: Conversion creation page
  if (view === 'create' && currentConversion) {
    const totalLangdi = calculateTotalLangdiWeight(currentConversion.entries);
    const totalPure = calculateTotalPureWeight(currentConversion.entries);

    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Create Conversion: {currentConversion.receiptNo}</h2>
          <button className="btn btn-secondary" onClick={() => {
            setView('list');
            setCurrentConversion(null);
          }}>
            Back to List
          </button>
        </div>

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Conversion Details</h5>
            <p><strong>Conversion Number:</strong> {currentConversion.conversionNumber}</p>
            <p><strong>Receipt No:</strong> {currentConversion.receiptNo}</p>
            <p><strong>Date:</strong> {formatDate(currentConversion.date)}</p>
            <p><strong>Status:</strong> <span className="badge bg-warning">{currentConversion.status}</span></p>

            <div className="table-responsive mt-4">
              <table className="table table-bordered">
                <thead className="table-dark">
                  <tr>
                    <th>Langdi Weight (gms)</th>
                    <th>Touch</th>
                    <th>Pure Weight (gms)</th>
                    <th>Receipt No</th>
                    <th>Client Name</th>
                  </tr>
                </thead>
                <tbody>
                  {currentConversion.entries.map((entry, index) => {
                    // Check if this entry is from an 'adding' type transaction
                    // Use stored transactionType in entry, or check from originalTransaction
                    // Works for both pending and completed conversions
                    const originalTransaction = data.find(t => 
                      t._id?.toString() === entry.transactionId?.toString() || 
                      t._id === entry.transactionId
                    );
                    // Check multiple ways to identify 'adding' type:
                    // 1. Stored transactionType in entry
                    // 2. Transaction type from fetched transaction
                    // 3. Fallback: if clientName is 'N/A' or empty, likely 'adding' type
                    // Allow editing if it's an 'adding' type transaction
                    // Touch can be edited even for saved conversions, but only if touch is still null/N/A
                    // Once touch is entered and saved, it should not be editable
                    const hasNullTouch = entry.touch === null || entry.touch === undefined;
                    const hasNoClientName = entry.clientName === 'N/A' || !entry.clientName || entry.clientName === '';
                    // In create view, allow editing if it's an 'adding' type transaction
                    // or if it has no client name (typical for 'adding' type)
                    const isAddingType = entry.transactionType === 'adding' || 
                                        originalTransaction?.type === 'adding' ||
                                        (view === 'create' && entry.transactionId && hasNoClientName) ||
                                        (hasNullTouch && hasNoClientName);
                    // Allow editing if it's an 'adding' type AND touch is still null/N/A (not set)
                    // Once touch has a value (including 0), it cannot be edited again
                    // Check if touch is a valid number - if it is, it's been set and cannot be edited
                    const touchIsSet = entry.touch !== null && entry.touch !== undefined && entry.touch !== '' && !isNaN(parseFloat(entry.touch));
                    const canEditTouch = isAddingType && !touchIsSet;
                    
                    // Create unique key for this input
                    const touchInputKey = `${entry.transactionId}-${entry.entryIndex !== undefined ? entry.entryIndex : index}`;
                    const isEditing = editingTouch[touchInputKey] !== undefined;
                    const displayValue = isEditing ? editingTouch[touchInputKey] : (entry.touch !== null && entry.touch !== undefined ? entry.touch : '');
                    
                    return (
                      <tr key={index}>
                        <td>{formatNumber(entry.langdiWeight, 3)}</td>
                        <td>
                          {canEditTouch ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={displayValue}
                              placeholder="Enter touch"
                              step="0.01"
                              min="0"
                              max="100"
                              onChange={(e) => {
                                // Update local state only, don't call API yet
                                const newValue = e.target.value;
                                setEditingTouch(prev => ({
                                  ...prev,
                                  [touchInputKey]: newValue
                                }));
                              }}
                              onBlur={(e) => {
                                // Only update on blur to prevent closing input
                                const touchValue = e.target.value === '' ? null : parseFloat(e.target.value);
                                // Use entryIndex from entry, or fallback to array index
                                const entryIndexToUse = entry.entryIndex !== undefined ? entry.entryIndex : index;
                                if (entry.transactionId) {
                                  handleUpdateTouch(entry.transactionId, entryIndexToUse, touchValue, 'adding');
                                }
                                // Clear local editing state after blur
                                setEditingTouch(prev => {
                                  const newState = { ...prev };
                                  delete newState[touchInputKey];
                                  return newState;
                                });
                              }}
                              onKeyDown={(e) => {
                                // Handle Enter key to blur
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                              style={{ width: '80px' }}
                            />
                          ) : (
                            entry.touch !== null ? formatNumber(entry.touch, 2) + '%' : 'N/A'
                          )}
                        </td>
                        <td>{entry.calculatedPureWeight !== null ? formatNumber(entry.calculatedPureWeight, 3) : 'N/A'}</td>
                        <td>{entry.receiptNo || 'N/A'}</td>
                        <td>{entry.clientName || 'N/A'}</td>
                      </tr>
                    );
                  })}
                  <tr className="table-info fw-bold">
                    <td>Total: {formatNumber(totalLangdi, 3)}</td>
                    <td>-</td>
                    <td>Total: {formatNumber(totalPure, 3)}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4 d-flex gap-2">
              <button className="btn btn-success btn-lg" onClick={handleSaveConversion}>
                Convert
              </button>
              <button className="btn btn-primary btn-lg" onClick={() => handlePrint(currentConversion)}>
                Print/Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render: Edit conversion page
  if (view === 'edit' && currentConversion) {
    const totalLangdi = calculateTotalLangdiWeight(currentConversion.entries);
    const totalPure = calculateTotalPureWeight(currentConversion.entries);

    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Edit Conversion: {currentConversion.receiptNo}</h2>
          <button className="btn btn-secondary" onClick={async () => {
            // Refresh conversions list to get latest data
            try {
              const convResponse = await fetch(`${baseURL}/api/conversions/all`);
              const convData = await convResponse.json();
              setConversions(convData);
            } catch (err) {
              console.error('Error refreshing conversions:', err);
            }
            setView('conversions');
            setCurrentConversion(null);
            setFinalPureWeight('');
          }}>
            Back to Conversions
          </button>
        </div>

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Conversion Details</h5>
            <p><strong>Conversion Number:</strong> {currentConversion.conversionNumber}</p>
            <p><strong>Receipt No:</strong> {currentConversion.receiptNo}</p>
            <p><strong>Date:</strong> {formatDate(currentConversion.date)}</p>
            <p><strong>Status:</strong> <span className={`badge ${currentConversion.status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
              {currentConversion.status}
            </span></p>

            <div className="table-responsive mt-4">
              <table className="table table-bordered">
                <thead className="table-dark">
                  <tr>
                    <th>Langdi Weight (gms)</th>
                    <th>Touch</th>
                    <th>Pure Weight (gms)</th>
                    <th>Receipt No</th>
                    <th>Client Name</th>
                  </tr>
                </thead>
                <tbody>
                  {currentConversion.entries.map((entry, index) => {
                    // Check if this entry is from an 'adding' type transaction
                    // Use stored transactionType in entry, or check from originalTransaction
                    // Works for both pending and completed conversions
                    const originalTransaction = data.find(t => 
                      t._id?.toString() === entry.transactionId?.toString() || 
                      t._id === entry.transactionId
                    );
                    // Check multiple ways to identify 'adding' type:
                    // 1. Stored transactionType in entry
                    // 2. Transaction type from fetched transaction
                    // 3. Fallback: if clientName is 'N/A' or empty, likely 'adding' type
                    // Allow editing if it's an 'adding' type transaction
                    // Touch can be edited even for completed conversions, but only if touch is still null/N/A
                    // Once touch is entered and saved, it should not be editable
                    const hasNullTouch = entry.touch === null || entry.touch === undefined;
                    const hasNoClientName = entry.clientName === 'N/A' || !entry.clientName || entry.clientName === '';
                    // In edit view, allow editing if it's an 'adding' type transaction
                    // or if it has no client name (typical for 'adding' type)
                    const isAddingType = entry.transactionType === 'adding' || 
                                        originalTransaction?.type === 'adding' ||
                                        (view === 'edit' && entry.transactionId && hasNoClientName) ||
                                        (hasNullTouch && hasNoClientName);
                    // Allow editing if it's an 'adding' type AND touch is still null/N/A (not set)
                    // Once touch has a value (including 0), it cannot be edited again
                    // Check if touch is a valid number - if it is, it's been set and cannot be edited
                    const touchIsSet = entry.touch !== null && entry.touch !== undefined && entry.touch !== '' && !isNaN(parseFloat(entry.touch));
                    const canEditTouch = isAddingType && !touchIsSet;
                    
                    // Create unique key for this input
                    const touchInputKey = `${entry.transactionId}-${entry.entryIndex !== undefined ? entry.entryIndex : index}`;
                    const isEditing = editingTouch[touchInputKey] !== undefined;
                    const displayValue = isEditing ? editingTouch[touchInputKey] : (entry.touch !== null && entry.touch !== undefined ? entry.touch : '');
                    
                    return (
                      <tr key={index}>
                        <td>{formatNumber(entry.langdiWeight, 3)}</td>
                        <td>
                          {canEditTouch ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={displayValue}
                              placeholder="Enter touch"
                              step="0.01"
                              min="0"
                              max="100"
                              onChange={(e) => {
                                // Update local state only, don't call API yet
                                const newValue = e.target.value;
                                setEditingTouch(prev => ({
                                  ...prev,
                                  [touchInputKey]: newValue
                                }));
                              }}
                              onBlur={(e) => {
                                // Only update on blur to prevent closing input
                                const touchValue = e.target.value === '' ? null : parseFloat(e.target.value);
                                // Use entryIndex from entry, or fallback to array index
                                const entryIndexToUse = entry.entryIndex !== undefined ? entry.entryIndex : index;
                                if (entry.transactionId) {
                                  handleUpdateTouch(entry.transactionId, entryIndexToUse, touchValue, 'adding');
                                }
                                // Clear local editing state after blur
                                setEditingTouch(prev => {
                                  const newState = { ...prev };
                                  delete newState[touchInputKey];
                                  return newState;
                                });
                              }}
                              onKeyDown={(e) => {
                                // Handle Enter key to blur
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                              style={{ width: '80px' }}
                            />
                          ) : (
                            entry.touch !== null ? formatNumber(entry.touch, 2) + '%' : 'N/A'
                          )}
                        </td>
                        <td>{entry.calculatedPureWeight !== null ? formatNumber(entry.calculatedPureWeight, 3) : 'N/A'}</td>
                        <td>{entry.receiptNo || 'N/A'}</td>
                        <td>{entry.clientName || 'N/A'}</td>
                      </tr>
                    );
                  })}
                  <tr className="table-info fw-bold">
                    <td>Total: {formatNumber(totalLangdi, 3)}</td>
                    <td>-</td>
                    <td>Total: {formatNumber(totalPure, 3)}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4">
              <div className="mb-3">
                <label htmlFor="finalPureWeight" className="form-label">
                  <strong>Actual Pure Weight (gms):</strong>
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="form-control"
                  id="finalPureWeight"
                  value={finalPureWeight}
                  onChange={(e) => setFinalPureWeight(e.target.value)}
                  placeholder="Enter actual pure weight"
                  disabled={currentConversion.status === 'completed'}
                />
                <small className="form-text text-muted">
                  Calculated Pure Weight: {formatNumber(totalPure, 3)} gms
                </small>
              </div>
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-success btn-lg" 
                  onClick={handleFinalizeConversion}
                  disabled={currentConversion.status === 'completed'}
                >
                  {currentConversion.status === 'completed' ? 'Already Completed' : 'Convert & Update Stock'}
                </button>
                <button className="btn btn-primary btn-lg" onClick={() => handlePrint(currentConversion)}>
                  Print/Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render: All conversions list
  if (view === 'conversions') {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>All Conversions</h1>
          <button className="btn btn-primary" onClick={() => setView('list')}>
            Back to Langdi Weight List
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-striped table-bordered">
            <thead className="table-dark">
              <tr>
                <th>Conversion No</th>
                <th>Receipt No</th>
                <th>Date</th>
                <th>Total Langdi (gms)</th>
                <th>Total Pure (gms)</th>
                <th>Final Pure (gms)</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {conversions.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">No conversions found</td>
                </tr>
              ) : (
                conversions.map((conversion) => {
                  const totalLangdi = calculateTotalLangdiWeight(conversion.entries);
                  return (
                    <tr key={conversion._id}>
                      <td>{conversion.conversionNumber}</td>
                      <td>{conversion.receiptNo}</td>
                      <td>{formatDate(conversion.date)}</td>
                      <td>{formatNumber(totalLangdi, 3)}</td>
                      <td>{formatNumber(conversion.totalCalculatedPureWeight, 3)}</td>
                      <td>{conversion.finalPureWeight !== null ? formatNumber(conversion.finalPureWeight, 3) : 'N/A'}</td>
                      <td>
                        <span className={`badge ${conversion.status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                          {conversion.status}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEditConversion(conversion)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-secondary"
                            onClick={() => handlePrint(conversion)}
                          >
                            Print
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return null;
};

export default LangdiWeightPage;
